import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _assertThisInitialized from "@babel/runtime/helpers/assertThisInitialized";
import _inherits from "@babel/runtime/helpers/inherits";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _extends from "@babel/runtime/helpers/extends";

function _createSuper(Derived) {
  function isNativeReflectConstruct() {
    if (typeof Reflect === "undefined" || !Reflect.construct) return false;
    if (Reflect.construct.sham) return false;
    if (typeof Proxy === "function") return true;

    try {
      Date.prototype.toString.call(Reflect.construct(Date, [], function () {}));
      return true;
    } catch (e) {
      return false;
    }
  }

  return function () {
    var Super = _getPrototypeOf(Derived),
        result;

    if (isNativeReflectConstruct()) {
      var NewTarget = _getPrototypeOf(this).constructor;

      result = Reflect.construct(Super, arguments, NewTarget);
    } else {
      result = Super.apply(this, arguments);
    }

    return _possibleConstructorReturn(this, result);
  };
}

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import ClearIcon from '../icons/forms/ClearIcon';
import VisibilityAnimation from '../VisibilityAnimation';
import OnClickOutside from '../OnClickOutside';
import FocusManager from '../FocusManager';
import renderToLayer from '../hoc/render-to-layer';
import zIndexStack from '../hoc/z-index-stack';
import { ESCAPE } from '../constants/keys';
import { POPUP_ZINDEX } from '../constants/z-indexes';
import { withStyles } from '../theme';
import compose from '../utils/compose';
import { isolateMixin, middleMixin, focusSourceMixin, ifDesktop, ifMobile } from '../utils/mixins';
import { subscribeFocusEvents } from '../utils/focus-source';
import createGetMemoizedRef from '../utils/createGetMemoizedRef';
subscribeFocusEvents();

var styles = function styles(theme) {
  return {
    backdrop: {
      extend: [isolateMixin, middleMixin],
      fontFamily: theme.fontFamily,
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      paddingTop: 20,
      paddingBottom: 20,
      textAlign: 'center',
      overflowY: 'auto',
      overflowX: 'hidden',
      marginTop: -10,
      opacity: 0,
      transitionDuration: theme.popup.animationDuration,
      transitionProperty: 'margin-top, opacity'
    },
    'backdrop-black': {
      backgroundColor: theme.popup.colors.backdrop.default
    },
    'backdrop-blue': {
      backgroundColor: theme.popup.colors.backdrop.blue
    },
    isVisible: {
      marginTop: 0,
      opacity: 1
    },
    popup: _extends({
      position: 'relative',
      display: 'inline-block',
      boxSizing: 'border-box',
      borderRadius: theme.popup.borderRadius,
      boxShadow: theme.popup.boxShadow,
      padding: [20, 25, 25],
      color: theme.popup.colors.text,
      minWidth: 300,
      maxWidth: 'calc(100% - 20px)',
      marginLeft: 10,
      marginRight: 10,
      backgroundColor: theme.popup.colors.background,
      fontSize: theme.popup.text.fontSize,
      lineHeight: "".concat(theme.popup.text.lineHeight, "px"),
      textAlign: 'left',
      outline: 'none'
    }, ifDesktop({
      minWidth: 350,
      maxWidth: 'auto'
    })),
    title: {
      marginBottom: 20,
      paddingRight: 25,
      fontSize: theme.popup.title.fontSize,
      lineHeight: "".concat(theme.popup.title.lineHeight, "px"),
      fontWeight: 500
    },
    close: _extends({
      position: 'absolute',
      top: 25,
      right: 25,
      border: 0,
      margin: 0,
      padding: 0,
      width: 15,
      height: 15,
      background: 'transparent',
      outline: 0,
      color: theme.popup.colors.close.default,
      cursor: 'pointer',
      '&:hover': {
        color: theme.popup.colors.close.hover
      }
    }, focusSourceMixin('other', '&:focus', {
      color: theme.popup.colors.close.hover
    }), {}, ifMobile({
      top: 15,
      right: 15
    })),
    buttons: {
      display: 'flex',
      justifyContent: 'space-between',
      marginTop: 30,
      '& > *': {
        flexGrow: 1,
        width: '50%'
      },
      '& > * + *': _extends({
        marginLeft: 10
      }, ifDesktop({
        marginLeft: 20
      }))
    }
  };
};

var _ref = /*#__PURE__*/React.createElement(ClearIcon, {
  size: 15,
  color: "currentColor"
});

var Popup = /*#__PURE__*/function (_PureComponent) {
  _inherits(Popup, _PureComponent);

  var _super = _createSuper(Popup);

  function Popup() {
    var _this;

    _classCallCheck(this, Popup);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _this.onWillVisible = function () {
      window.addEventListener('keydown', _this.onKeyDown);
    };

    _this.onWillInvisible = function () {
      window.removeEventListener('keydown', _this.onKeyDown);
    };

    _this.onKeyDown = function (event) {
      var _assertThisInitialize = _assertThisInitialized(_this),
          closeOnEsc = _assertThisInitialize.closeOnEsc,
          onRequestClose = _assertThisInitialize.onRequestClose;

      if (closeOnEsc && event.keyCode === ESCAPE) onRequestClose();
    };

    _this.getMemoizedRef = createGetMemoizedRef();

    _this.renderContent = function (componentRef) {
      var _this$props = _this.props,
          children = _this$props.children,
          className = _this$props.className,
          style = _this$props.style,
          title = _this$props.title,
          titleStyle = _this$props.titleStyle,
          titleClassName = _this$props.titleClassName,
          buttonsContainerStyle = _this$props.buttonsContainerStyle,
          buttonsContainerClassName = _this$props.buttonsContainerClassName,
          classes = _this$props.classes,
          showClose = _this$props.showClose,
          okButton = _this$props.okButton,
          tabIndex = _this$props.tabIndex,
          cancelButton = _this$props.cancelButton,
          onRequestClose = _this$props.onRequestClose,
          theme = _this$props.theme;
      return React.createElement(FocusManager, {
        tabIndex: tabIndex
      }, function (focusRef) {
        return React.createElement("div", {
          style: style,
          className: classnames(classes.popup, className),
          ref: _this.getMemoizedRef(focusRef, componentRef)
        }, showClose && React.createElement("button", {
          className: classes.close,
          onClick: onRequestClose,
          "aria-label": theme.i18n.closePopup
        }, _ref), title && React.createElement("header", {
          style: titleStyle,
          className: classnames(classes.title, titleClassName)
        }, title), children, (okButton || cancelButton) && React.createElement("footer", {
          style: buttonsContainerStyle,
          className: classnames(classes.buttons, buttonsContainerClassName)
        }, okButton, cancelButton));
      });
    };

    return _this;
  }

  _createClass(Popup, [{
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.onWillInvisible();
    }
  }, {
    key: "renderPopup",
    value: function renderPopup() {
      var _this2 = this;

      var _this$props2 = this.props,
          closeOnClickOutside = _this$props2.closeOnClickOutside,
          onRequestClose = _this$props2.onRequestClose;
      if (closeOnClickOutside) return React.createElement(OnClickOutside, {
        handler: onRequestClose
      }, function (componentRef) {
        return _this2.renderContent(componentRef);
      });
      return this.renderContent();
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;

      var _this$props3 = this.props,
          isOpened = _this$props3.isOpened,
          backdropClassName = _this$props3.backdropClassName,
          backdropStyle = _this$props3.backdropStyle,
          backdropColor = _this$props3.backdropColor,
          theme = _this$props3.theme,
          classes = _this$props3.classes,
          onOpen = _this$props3.onOpen,
          onClose = _this$props3.onClose;
      return React.createElement(VisibilityAnimation, {
        isVisible: isOpened,
        animationDuration: theme.popup.animationDuration,
        onWillVisible: this.onWillVisible,
        onVisible: onOpen,
        onWillInvisible: this.onWillInvisible,
        onInvisible: onClose
      }, function (_ref2) {
        var isVisible = _ref2.isVisible;
        return React.createElement("div", {
          ref: function ref(el) {
            _this3.backdrop = el;
          },
          style: backdropStyle,
          className: classnames(classes.backdrop, classes["backdrop-".concat(backdropColor)], backdropClassName, isVisible && classes.isVisible)
        }, _this3.renderPopup());
      });
    }
  }]);

  return Popup;
}(PureComponent);

Popup.defaultProps = {
  isOpened: false,
  showClose: true,
  closeOnEsc: true,
  closeOnClickOutside: true,
  backdropColor: 'black',
  tabIndex: 0,
  onOpen: function onOpen() {},
  onRequestClose: function onRequestClose() {},
  onClose: function onClose() {}
};
Popup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Css-класс
   */
  className: PropTypes.string,

  /**
   * Inline-стили
   */
  style: PropTypes.object,

  /**
   * Css-класс для фонового слоя
   */
  backdropClassName: PropTypes.string,

  /**
   * Inline-стили для фонового слоя
   */
  backdropStyle: PropTypes.object,

  /**
   * Цвет фонового слоя
   */
  backdropColor: PropTypes.oneOf(['black', 'blue']),

  /**
   * Контент для попапа
   */
  children: PropTypes.node,

  /**
   * Заголовок
   */
  title: PropTypes.node,

  /**
   * Css-класс для заголовка
   */
  titleClassName: PropTypes.string,

  /**
   * Inline-стили для заголовка
   */
  titleStyle: PropTypes.object,

  /**
   * Css-класс блока с кнопками
   */
  buttonsContainerClassName: PropTypes.string,

  /**
   * Inline-стили блока с кнопками
   */
  buttonsContainerStyle: PropTypes.object,

  /**
   * Контролирует видимость попапа
   */
  isOpened: PropTypes.bool,

  /**
   * Порядок фокусировки элемента
   */
  tabIndex: PropTypes.number,

  /**
   * Кнопка успешного действия (если она одна, то будет расятнута на все ширину)
   */
  okButton: PropTypes.node,

  /**
   * Кнопка отмены
   */
  cancelButton: PropTypes.node,

  /**
   * Кнопка закрытия попапа
   */
  showClose: PropTypes.bool,

  /**
   * Закрытие попапа кнопкой esc
   */
  closeOnEsc: PropTypes.bool,

  /**
   * Закрытие попапа по клику вне него
   */
  closeOnClickOutside: PropTypes.bool,

  /**
   * Коллбек вызывающийся после открытия попапа
   */
  onOpen: PropTypes.func,

  /**
   * Коллбек вызывающийся при нажатии на крестик (автоматически проставляется, если используется `@providePopup`)
   */
  onRequestClose: PropTypes.func,

  /**
   * Коллбек вызывающийся после закрытия попапа
   */
  onClose: PropTypes.func
} : {};
export default compose(zIndexStack(POPUP_ZINDEX), renderToLayer, withStyles(styles, {
  name: 'Popup'
}))(Popup);